import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@mui/material';
import GamizignLogoZpart from '../../assets/images/gamizign logo - Z.png';
import GamizignLogo from '../../assets/images/gamizign logo adobe com without text 500p.webp';

const LoadingComponent = ({ fullScreen = false, messageType = 'default', progress = null }) => {
  const { i18n } = useTranslation();
  const [loadingStep, setLoadingStep] = useState(0);

  // Define sequential reassuring messages per context and language
  const loadingMessages = {
    course: {
      en: [
        { title: "Initializing AI Engine", text: "Connecting to the high-performance Gemini AI model..." },
        { title: "Analyzing Subject Content", text: "Processing target grade level, subject, and subtopics..." },
        { title: "Mapping Learning Objectives", text: "Matching curriculum benchmarks and target goals..." },
        { title: "Designing Course Architecture", text: "Structuring game stages, activities, and worlds..." },
        { title: "Crafting Interactive Questions", text: "Generating custom gamified questions & detailed explanations..." },
        { title: "Finalizing Student Map", text: "Structuring your gamified student learning portal... almost there!" }
      ],
      ar: [
        { title: "تهيئة محرك الذكاء الاصطناعي", text: "الاتصال بنموذج Gemini AI عالي الأداء..." },
        { title: "تحليل محتوى المادة", text: "معالجة الصف الدراسي، المادة الدراسية، والمواضيع الفرعية..." },
        { title: "رسم أهداف التعلم", text: "مطابقة معايير المنهج الدراسي والأهداف التعليمية..." },
        { title: "تصميم هيكل الدورة", text: "هيكلة مراحل اللعبة، الأنشطة، والعوالم التفاعلية..." },
        { title: "صياغة الأسئلة التفاعلية", text: "إنشاء أسئلة مخصصة تفاعلية وشروحات مفصلة للطلاب..." },
        { title: "إنهاء خريطة الطالب", text: "هيكلة بوابة تعلم الطالب التفاعلية... على وشك الانتهاء!" }
      ]
    },
    pdf: {
      en: [
        { title: "Reading PDF Document", text: "Uploading files and analyzing page structures..." },
        { title: "Extracting Subtopics", text: "Analyzing slide content and identifying educational concepts..." },
        { title: "Formulating Question Sets", text: "Aligning learning materials with selected difficulty levels..." },
        { title: "Creating Active Activities", text: "Designing questions, games, and explanations..." },
        { title: "Assembling Custom Stages", text: "Setting up your gamified customize screens... just a moment!" }
      ],
      ar: [
        { title: "قراءة مستند PDF", text: "جاري تحميل الملفات وتحليل بنية الصفحات..." },
        { title: "استخراج المواضيع الفرعية", text: "تحليل محتوى الشرائح وتحديد المفاهيم التعليمية..." },
        { title: "صياغة مجموعات الأسئلة", text: "مطابقة المواد التعليمية مع مستويات الصعوبة المحددة..." },
        { title: "إنشاء الأنشطة التفاعلية", text: "تصميم الأسئلة التفاعلية، الألعاب، والشروحات..." },
        { title: "تجميع المراحل المخصصة", text: "إعداد شاشات التخصيص التفاعلية... لحظة واحدة فقط!" }
      ]
    },
    pptx: {
      en: [
        { title: "Reading Presentation Slides", text: "Uploading and processing slides and image contents..." },
        { title: "Parsing Slide Context", text: "Extracting outlines and learning concepts from each slide..." },
        { title: "Drafting Question Banks", text: "Aligning curriculum with selected difficulty levels..." },
        { title: "Structuring Game Activities", text: "Polishing answer choices and explanation cues..." },
        { title: "Polishing Game Stages", text: "Structuring your gamified stages... preparing customized draft!" }
      ],
      ar: [
        { title: "قراءة شرائح العرض التقديمي", text: "جاري تحميل ومعالجة الشرائح ومحتويات الصور..." },
        { title: "تحليل سياق الشرائح", text: "استخراج الخطوط العريضة ومفاهيم التعلم من كل شريحة..." },
        { title: "صياغة بنوك الأسئلة", text: "مطابقة المنهج الدراسي مع مستويات الصعوبة المحددة..." },
        { title: "هيكلة أنشطة اللعب", text: "تلميع خيارات الإجابة وإشارات الشرح التعليمية..." },
        { title: "تلميع مراحل اللعب", text: "هيكلة مراحل اللعبة التفاعلية... جاري إعداد المسودة المخصصة!" }
      ]
    },
    default: {
      en: [
        { title: "Active Learning Mechanics", text: "Structuring game-based learning steps..." },
        { title: "Pedagogical Goals", text: "Aligning student activities with curriculum standards..." },
        { title: "Interactive Dashboards", text: "Configuring customized reporting databases..." },
        { title: "Loading Assets", text: "Polishing gamified elements and styling pages..." }
      ],
      ar: [
        { title: "آليات التعلم النشط", text: "هيكلة خطوات التعلم القائم على اللعب..." },
        { title: "الأهداف التربوية", text: "ربط أنشطة الطلاب بمعايير المنهج الدراسي..." },
        { title: "لوحات التحكم التفاعلية", text: "تهيئة قواعد بيانات التقارير المخصصة..." },
        { title: "تحميل العناصر والأصول", text: "تلميع أصول اللعبة التفاعلية وتنسيق الصفحات..." }
      ]
    }
  };

  const currentLang = i18n.language === 'ar' ? 'ar' : 'en';
  const selectedTypeMessages = loadingMessages[messageType] || loadingMessages.default;
  const messagesList = selectedTypeMessages[currentLang] || selectedTypeMessages['en'];

  useEffect(() => {
    const interval = setInterval(() => {
      setLoadingStep((prev) => (prev + 1) % messagesList.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [messagesList.length]);

  const currentMsg = messagesList[loadingStep] || messagesList[0];

  // Visual layout for fullScreen premium glassmorphic loader
  if (fullScreen) {
    return (
      <div className="fixed inset-0 z-[9999] flex flex-col justify-center items-center bg-slate-900/65 backdrop-blur-md h-full w-full px-6 transition-all duration-300">
        <div className="bg-white/95 border border-slate-100 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-2xl p-8 max-w-md w-full text-center relative overflow-hidden flex flex-col items-center gap-6 transform transition-all duration-300">
          {/* Top animated background gradient bar */}
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500 animate-pulse"></div>

          {/* Gamizign Animated Spinning Logo */}
          <div className="relative flex items-center justify-center h-24 w-24">
            <CircularProgress size={96} thickness={2} className="text-indigo-600 absolute" />
            <div className="relative h-16 w-16 flex items-center justify-center">
              <img src={GamizignLogo} width="64" height="64" alt="Gamizign logo" />
              <img
                src={GamizignLogoZpart}
                width="26"
                height="26"
                className="animate-spin absolute top-[19px] left-[19px]"
                alt="Z"
              />
            </div>
          </div>

          {/* Progress Steps & Reassurance */}
          <div className="flex flex-col gap-2 w-full text-center">
            <span className="text-[10px] uppercase tracking-widest text-indigo-600 font-extrabold animate-pulse">
              {i18n.language === 'ar' ? "جاري الإنشاء بالذكاء الاصطناعي" : "AI ENGINE ACTIVE"}
            </span>
            <h4 className="text-base font-bold text-slate-800 transition-all duration-300">
              {currentMsg.title}
            </h4>
            <p className="text-xs text-slate-500 min-h-[40px] leading-relaxed transition-all duration-300 px-4">
              {currentMsg.text}
            </p>
          </div>

          {/* Steps timeline dots */}
          <div className="flex gap-2 justify-center mt-1">
            {messagesList.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === loadingStep ? 'w-5 bg-indigo-600' : 'w-1.5 bg-slate-200'
                }`}
              ></div>
            ))}
          </div>

          {/* Real-time numerical progress if supplied */}
          {progress !== null && progress > 0 && (
            <div className="w-full flex flex-col gap-1 px-4 mt-1">
              <div className="flex justify-between text-[10px] font-bold text-slate-500">
                <span>{i18n.language === 'ar' ? 'التقدم' : 'Progress'}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-indigo-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Small reassure notice */}
          <p className="text-[10px] text-slate-400 mt-1">
            {i18n.language === 'ar'
              ? "قد تستغرق هذه العملية ما بين 15 إلى 35 ثانية. يرجى إبقاء هذه الصفحة مفتوحة."
              : "This process usually takes 15-35 seconds. Please keep this tab active."}
          </p>
        </div>
      </div>
    );
  }

  // Visual layout for inline standard loader inside standard dashboard panels
  return (
    <div className="flex flex-col items-center justify-center p-6 min-h-[140px] w-full text-center relative gap-3 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
      <div className="relative flex items-center justify-center h-16 w-16">
        <CircularProgress size={64} thickness={2} className="text-slate-400 absolute" />
        <div className="relative h-10 w-10 flex items-center justify-center">
          <img src={GamizignLogo} width="40" height="40" alt="Gamizign logo" />
          <img
            src={GamizignLogoZpart}
            width="16"
            height="16"
            className="animate-spin absolute top-[12px] left-[12px]"
            alt="Z"
          />
        </div>
      </div>
      
      <div className="flex flex-col gap-1 max-w-xs">
        <h5 className="text-xs font-semibold text-slate-600 transition-all duration-300">
          {currentMsg.title}
        </h5>
        <p className="text-[10px] text-slate-400 transition-all duration-300">
          {currentMsg.text}
        </p>
      </div>
    </div>
  );
};

export default LoadingComponent;